import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Paper, Typography, TextField, Autocomplete, Button, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Snackbar, Alert } from '@mui/material';
import { caseService, Case } from '../services/caseService';
import { firmAPI, userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { isPmpOrSiteAdminUser } from '../utils/firmAccess';

const VendorAssign: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAssignVendor =
    isPmpOrSiteAdminUser(user) ||
    !!user?.roles?.some((role) => role.slug === 'client_manager');
  const [cases, setCases] = useState<Case[]>([]);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [vendorUsers, setVendorUsers] = useState<any[]>([]);
  const [vendorUserId, setVendorUserId] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [toast, setToast] = useState<{open:boolean; message:string; severity:'success'|'error'|'info'|'warning'}>({open:false,message:'',severity:'success'});

  useEffect(() => {
    if (user && !canAssignVendor) {
      navigate('/', { replace: true });
    }
  }, [user, canAssignVendor, navigate]);

  useEffect(() => {
    if (!firmId || !canAssignVendor) return;
    (async () => {
      const casesRes = await caseService.getFirmCases(Number(firmId));
      setCases(casesRes.data.data || casesRes.data || []);
      const firmsRes = await firmAPI.getAll({ type: 'vendor', per_page: 1000 });
      const vendors = (firmsRes.data.data?.data || firmsRes.data.data || []).filter((f: any) => f.type === 'vendor');
      setVendorOptions(vendors);
    })();
  }, [firmId, canAssignVendor]);

  useEffect(() => {
    if (!vendorId) { setVendorUsers([]); setVendorUserId(null); return; }
    (async () => {
      try {
        const res = await firmAPI.members(vendorId);
        const list = (res.data.data || []).map((u: any) => ({ id: u.id, name: `${u.name} (${u.email})` }));
        setVendorUsers(list);
      } catch { setVendorUsers([]); }
    })();
  }, [vendorId]);

  const filtered = useMemo(() => {
    return cases.filter(c => {
      if (title && !c.title.toLowerCase().includes(title.toLowerCase())) return false;
      if (subtitle && !(c.subtitle || '').toLowerCase().includes(subtitle.toLowerCase())) return false;
      if (status && String(c.status).toLowerCase() !== status) return false;
      return true;
    });
  }, [cases, title, subtitle, status]);

  const toggleAll = (checked: boolean) => {
    if (!checked) { setSelectedIds([]); return; }
    // Only select cases that are not already vendor-assigned
    const selectable = filtered.filter((c: any) => !c.assigned_vendor_id).map((c) => c.id);
    setSelectedIds(selectable);
  };

  const toggleOne = (id: number, checked: boolean, disabled?: boolean) => {
    if (disabled) return;
    setSelectedIds(prev => checked ? Array.from(new Set([...prev, id])) : prev.filter(x => x !== id));
  };

  const handleAssign = async () => {
    if (!vendorId || selectedIds.length === 0) {
      setToast({open:true, message:'Select vendor and at least one case', severity:'warning'});
      return;
    }
    try {
      for (const id of selectedIds) {
        await caseService.assignVendor(id, { vendor_id: vendorId, vendor_user_id: vendorUserId || undefined, due_date: dueDate || undefined });
      }
      setToast({open:true, message:`Assigned ${selectedIds.length} case(s)`, severity:'success'});
      setSelectedIds([]);
    } catch (e: any) {
      setToast({open:true, message:e.message || 'Failed to assign', severity:'error'});
    }
  };

  if (user && !canAssignVendor) {
    return (
      <Box>
        <Alert severity="error">You do not have permission to assign vendors.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Assign Cases to Vendor</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Filter</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs:'1fr', md:'repeat(3, 1fr)'}, gap: 2 }}>
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <TextField label="Sub Title" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
          <Autocomplete options={['open','in_progress','completed','closed']} value={status} onChange={(_e,v)=>setStatus(v)} renderInput={(p)=><TextField {...p} label="Status"/>} />
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Result</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"><Checkbox checked={filtered.length>0 && selectedIds.length===filtered.length} indeterminate={selectedIds.length>0 && selectedIds.length<filtered.length} onChange={(_e,checked)=>toggleAll(checked)} /></TableCell>
              <TableCell>#</TableCell>
              <TableCell>Case ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((c: any) => {
              const isAssigned = Boolean(c.assigned_vendor_id);
              return (
              <TableRow key={c.id}>
                <TableCell padding="checkbox"><Checkbox checked={selectedIds.includes(c.id)} disabled={isAssigned} onChange={(_e,checked)=>toggleOne(c.id, checked, isAssigned)} title={isAssigned ? 'Already assigned to vendor' : ''} /></TableCell>
                <TableCell>•</TableCell>
                <TableCell>{c.id}</TableCell>
                <TableCell>{c.title}</TableCell>
                <TableCell>{c.project?.name || '—'}</TableCell>
                <TableCell>{String(c.status).toUpperCase()}</TableCell>
              </TableRow>
            );})}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Vendor List</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs:'1fr', md:'repeat(2, 1fr)'}, gap: 2, mb: 2 }}>
          <Autocomplete
            options={vendorOptions}
            getOptionLabel={(o) => o ? `${o.name}` : ''}
            value={vendorOptions.find(v => v.id === vendorId) || null}
            onChange={(_e, v:any) => setVendorId(v?.id || null)}
            renderInput={(p)=><TextField {...p} label="Vendor Firm" placeholder="Search vendors" />}
          />
          <Autocomplete
            options={vendorUsers}
            getOptionLabel={(o) => o?.name || ''}
            value={vendorUsers.find(u => u.id === vendorUserId) || null}
            onChange={(_e, v:any) => setVendorUserId(v?.id || null)}
            renderInput={(p)=><TextField {...p} label="Vendor User (optional)" />}
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs:'1fr', md:'300px' }, gap: 2, mb: 2 }}>
          <TextField
            label="Due Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={dueDate}
            onChange={(e)=>setDueDate(e.target.value)}
            helperText="Required unless the project allows optional due dates"
          />
        </Box>
        <Button variant="contained" onClick={handleAssign} disabled={!vendorId || selectedIds.length===0}>Assign</Button>
      </Paper>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast(prev=>({...prev, open:false}))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast(prev=>({...prev, open:false}))}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorAssign;


